import { CHANNEL_NAMES } from '@wms/domain';
import { packCount, type ChannelProductsCreated, type ChannelProductView, type ProductListItem } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import type { CredentialView } from '../../types';
import { Empty, ErrorBanner, Modal, ProductThumb, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';
import { SellerSelect, useSellerPick } from './SellerPick';

const CHANNEL: Readonly<Record<string, string>> = CHANNEL_NAMES;
type Status = 'unmatched' | 'matched' | 'all';

/**
 * Pazaryeri SKU eşleştirme: the seller's items on each marketplace (from the marketplace's
 * product list, or seen in an order that matched nothing) tied to the products in the warehouse.
 * Exact barcode / SKU matches are suggested and can be mapped at once; mapping releases the
 * orders that waited for the item. Items nothing matches can become products of the catalog:
 * a seller whose store is on the marketplace starts its catalog from there.
 */
export function Mapping() {
  const toast = useToast();
  const pick = useSellerPick();
  const tq = pick.query('&');
  const accounts = useLoad(
    () => (pick.ready ? api<CredentialView[]>('GET', `/api/mapping/accounts${pick.query('?')}`) : Promise.resolve([] as CredentialView[])),
    [pick.ready, pick.tenantId],
  );
  const [credentialId, setCredentialId] = useState('');
  // a single account is the one to work on; another seller's account is forgotten
  useEffect(() => {
    const list = accounts.data ?? [];
    if (list.length === 1 && !credentialId) setCredentialId(list[0]!.id);
    else if (credentialId && accounts.data && !list.some((a) => a.id === credentialId)) setCredentialId('');
  }, [accounts.data]);
  const [status, setStatus] = useState<Status>('unmatched');
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [linking, setLinking] = useState<ChannelProductView | null>(null);
  const [making, setMaking] = useState(false);
  const [made, setMade] = useState<ChannelProductsCreated | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const params = new URLSearchParams({ status, ...(credentialId ? { credentialId } : {}), ...(term ? { q: term } : {}) });
  const items = useLoad(
    () => (pick.ready ? api<ChannelProductView[]>('GET', `/api/mapping/items?${params}${tq}`) : Promise.resolve([] as ChannelProductView[])),
    [status, credentialId, term, pick.ready, pick.tenantId],
  );
  const all = useLoad(
    () => (pick.ready ? api<ChannelProductView[]>('GET', `/api/mapping/items?status=unmatched${credentialId ? `&credentialId=${credentialId}` : ''}${tq}`) : Promise.resolve([] as ChannelProductView[])),
    [credentialId, pick.ready, pick.tenantId],
  );
  const waiting = (all.data ?? []).reduce((n, i) => n + i.unmatchedOrders, 0);
  const suggested = (all.data ?? []).filter((i) => i.suggestion).length;
  // what nothing in the catalog matches; sets are mapped by a person, with their count
  const creatable = (all.data ?? []).filter((i) => !i.suggestion && !packCount(i.title)).length;

  const refresh = () => {
    void items.reload();
    void all.reload();
  };
  const released = (n: number) => (n > 0 ? ` ${n} bekleyen sipariş serbest bırakıldı.` : '');

  const sync = async () => {
    if (!credentialId) return toast('Önce bir pazaryeri hesabı seçin', 'info');
    setBusy(true);
    try {
      const r = await api<{ fetched: number; added: number }>('POST', '/api/mapping/items/sync', { credentialId, ...(pick.tenantId ? { tenantId: pick.tenantId } : {}) });
      toast(`${r.fetched} ürün okundu, ${r.added} yeni ürün listeye eklendi`, 'ok');
      refresh();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const auto = async () => {
    setBusy(true);
    try {
      const r = await api<{ linked: number; releasedOrders: number }>('POST', '/api/mapping/links/auto', {
        ...(credentialId ? { credentialId } : {}),
        ...(pick.tenantId ? { tenantId: pick.tenantId } : {}),
      });
      toast(r.linked > 0 ? `${r.linked} ürün barkod/SKU ile eşleştirildi.${released(r.releasedOrders)}` : 'Otomatik eşleşecek ürün kalmadı', 'ok');
      refresh();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const link = async (item: ChannelProductView, productId: string, unitsPerListing: number) => {
    const r = await api<{ releasedOrders: number }>('POST', '/api/mapping/links', {
      credentialId: item.credentialId,
      channelSku: item.channelSku,
      productId,
      unitsPerListing,
      ...(pick.tenantId ? { tenantId: pick.tenantId } : {}),
    });
    toast(`Eşleştirildi.${released(r.releasedOrders)}`, 'ok');
    refresh();
  };
  const makeProducts = async (items?: ChannelProductView[]) => {
    const credential = items?.[0]?.credentialId ?? credentialId;
    if (!credential) return toast('Önce bir pazaryeri hesabı seçin', 'info');
    setBusy(true);
    try {
      const r = await api<ChannelProductsCreated>('POST', '/api/mapping/items/products', {
        credentialId: credential,
        ...(items ? { channelSkus: items.map((i) => i.channelSku) } : {}),
        ...(pick.tenantId ? { tenantId: pick.tenantId } : {}),
      });
      setMaking(false);
      setMade(r.skipped.length > 0 ? r : null);
      toast(
        r.created > 0 ? `${r.created} ürün kataloğa eklendi ve eşleştirildi.${released(r.releasedOrders)}` : 'Yeni ürün eklenmedi',
        r.created > 0 ? 'ok' : 'info',
      );
      refresh();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const unlink = async (item: ChannelProductView) => {
    if (!item.listing || !window.confirm(`${item.title} ile ${item.listing.sku} arasındaki eşleştirme kaldırılsın mı?`)) return;
    try {
      await api('DELETE', `/api/mapping/links/${item.listing.id}${pick.query('?')}`);
      toast('Eşleştirme kaldırıldı', 'ok');
      refresh();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const sort = useSort<ChannelProductView>('mapping', {
    item: { label: 'Pazaryerindeki ürün', value: (i) => i.title },
    account: { label: 'Hesap', value: (i) => `${CHANNEL[i.channel] ?? i.channel} ${i.accountLabel}` },
    // the items orders wait for first
    source: { label: 'Kaynak', value: (i) => i.unmatchedOrders, desc: true },
    product: { label: 'Depodaki ürün', value: (i) => i.listing?.sku ?? i.suggestion?.sku },
  });
  const list = sort.apply(items.data ?? []);
  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/urunler">
          <Icon name="back" /> {pick.staff ? 'Ürünler' : 'Ürünler ve stok'}
        </a>
        <SellerSelect pick={pick} />
        <select className="select" style={{ maxWidth: 260 }} value={credentialId} onChange={(e) => setCredentialId(e.target.value)} aria-label="Pazaryeri hesabı">
          <option value="">Tüm pazaryeri hesapları</option>
          {(accounts.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {CHANNEL[a.channel] ?? a.channel} · {a.label}
            </option>
          ))}
        </select>
        <span className="spacer" style={{ flex: 1 }} />
        {pick.canEdit && (
          <div className="row" style={{ gap: 6 }}>
            <button className="btn" disabled={busy || !credentialId} title={credentialId ? undefined : 'Bir hesap seçin'} onClick={() => void sync()}>
              <Icon name="download" size={18} /> Pazaryerinden ürünleri çek
            </button>
            <button className="btn primary" disabled={busy || suggested === 0} onClick={() => void auto()}>
              <Icon name="link" size={18} /> Önerileri eşleştir{suggested > 0 ? ` (${suggested})` : ''}
            </button>
            <button
              className="btn"
              disabled={busy || !credentialId || creatable === 0}
              title={credentialId ? undefined : 'Bir hesap seçin'}
              onClick={() => setMaking(true)}
            >
              <Icon name="plus" size={18} /> Ürün olarak ekle{credentialId && creatable > 0 ? ` (${creatable})` : ''}
            </button>
          </div>
        )}
      </div>
      {!pick.ready && <div className="banner info">Eşleştirme yapılacak satıcıyı seçin.</div>}
      {pick.ready && accounts.data?.length === 0 && (
        <div className="banner warn">Bu satıcının bağlı pazaryeri hesabı yok. Hesaplar "Pazaryeri hesapları" ekranından bağlanır.</div>
      )}
      {made && (
        <div className="banner warn">
          <Icon name="alert" />
          <div style={{ flex: 1 }}>
            {made.skipped.length} ürün eklenmedi, elle eşleştirin:
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontWeight: 500 }}>
              {made.skipped.slice(0, 8).map((s) => (
                <li key={s.channelSku}>
                  {s.title} <span className="mono">({s.channelSku})</span>: {s.reason}
                </li>
              ))}
              {made.skipped.length > 8 && <li>ve {made.skipped.length - 8} ürün daha</li>}
            </ul>
          </div>
          <button className="icon-btn" onClick={() => setMade(null)} aria-label="Kapat">
            <Icon name="x" />
          </button>
        </div>
      )}
      {waiting > 0 && (
        <div className="banner warn">
          <Icon name="alert" />
          <div>
            {waiting} sipariş, eşleşmeyen ürün yüzünden bekliyor. Ürünü eşleştirdiğinizde siparişler kendiliğinden işleme girer.
          </div>
        </div>
      )}
      <div className="row">
        <div className="tabs" role="tablist">
          {(
            [
              ['unmatched', 'Eşleşmemiş'],
              ['matched', 'Eşleşmiş'],
              ['all', 'Tümü'],
            ] as const
          ).map(([key, label]) => (
            <button key={key} className={status === key ? 'on' : ''} role="tab" aria-selected={status === key} onClick={() => setStatus(key)}>
              {label}
              {key === 'unmatched' && all.data ? ` (${all.data.length})` : ''}
            </button>
          ))}
        </div>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Ürün adı, stok kodu veya barkod" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <section className="card">
        {items.error && <ErrorBanner message={items.error} onRetry={() => void items.reload()} />}
        {!items.data ? (
          <Spinner />
        ) : list.length === 0 ? (
          <Empty title={status === 'unmatched' ? 'Eşleşmemiş ürün yok' : 'Liste boş'}>
            {status === 'unmatched' ? '"Pazaryerinden ürünleri çek" ile mağazanızdaki ürünleri getirin; siparişte eşleşmeyen ürünler de burada görünür.' : null}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  {sort.th('item')}
                  {sort.th('account')}
                  {sort.th('source')}
                  {sort.th('product')}
                  {pick.canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {list.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="product compact">
                        <ProductThumb name={item.title} imageUrl={item.imageUrl} small />
                        <div style={{ minWidth: 0 }}>
                          <div className="name">{item.title}</div>
                          <div className="muted mono" style={{ fontSize: 13 }}>
                            {item.channelSku}
                            {item.barcode && item.barcode !== item.channelSku ? ` · ${item.barcode}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="chip">{CHANNEL[item.channel] ?? item.channel}</span>
                      <div className="muted" style={{ fontSize: 13 }}>
                        {item.accountLabel}
                      </div>
                    </td>
                    <td>
                      {item.source === 'api' ? <span className="chip">Ürün listesi</span> : <span className="chip">Siparişte görüldü</span>}
                      {item.unmatchedOrders > 0 && <div className="chip warn" style={{ marginTop: 4 }}>{item.unmatchedOrders} sipariş bekliyor</div>}
                    </td>
                    <td>
                      {item.listing ? (
                        <div>
                          <span className="chip ok">
                            <Icon name="check" size={14} /> {item.listing.sku}
                          </span>{' '}
                          {item.listing.unitsPerListing > 1 && <b>× {item.listing.unitsPerListing} adet</b>}
                          <div className="muted" style={{ fontSize: 13 }}>
                            {item.listing.name}
                          </div>
                        </div>
                      ) : item.suggestion ? (
                        <div>
                          <span className="chip info">Öneri: {item.suggestion.sku}</span>
                          <div className="muted" style={{ fontSize: 13 }}>
                            {item.suggestion.name} · {item.suggestion.via === 'barcode' ? 'barkod aynı' : 'stok kodu aynı'}
                          </div>
                        </div>
                      ) : (
                        <span className="muted">Eşleşmedi</span>
                      )}
                    </td>
                    {pick.canEdit && (
                      <td className="nowrap" style={{ textAlign: 'right' }}>
                        {item.listing ? (
                          <button className="btn sm" onClick={() => void unlink(item)}>
                            Kaldır
                          </button>
                        ) : (
                          <>
                            {item.suggestion && (
                              <button
                                className="btn sm primary"
                                onClick={() => void link(item, item.suggestion!.productId, item.suggestion!.unitsPerListing).catch((e) => toast(errorMessage(e), 'bad'))}
                              >
                                Bağla
                              </button>
                            )}{' '}
                            <button className="btn sm" onClick={() => setLinking(item)}>
                              Ürün seç
                            </button>
                            {!item.suggestion && (
                              <>
                                {' '}
                                <button className="btn sm" disabled={busy} title="Bu ürünü kataloğa ekleyip eşleştirir" onClick={() => void makeProducts([item])}>
                                  Ürün olarak ekle
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {making && (
        <Modal
          title="Ürün olarak ekle"
          onClose={() => setMaking(false)}
          footer={
            <>
              <button className="btn" onClick={() => setMaking(false)}>
                Vazgeç
              </button>
              <button className="btn primary" disabled={busy} onClick={() => void makeProducts()}>
                {creatable} ürünü ekle
              </button>
            </>
          }
        >
          <div className="stack">
            <p>
              Bu hesapta kataloğunuzdaki hiçbir ürünle eşleşmeyen <b>{creatable}</b> ürün, katalogda yeni ürün olarak açılır ve eşleştirilir.
            </p>
            <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
              <li>SKU pazaryerindeki stok kodu olur; ad, marka, görsel ve barkod da pazaryerinden gelir.</li>
              <li>“2’li set”, “6’lı paket” gibi ürünler eklenmez: onları “Ürün seç” ile adediyle eşleştirin.</li>
              <li>Ölçü ve ağırlığı sonra Ürünler ekranından ya da Excel ile tamamlayın; koli önerisi ve desi bunlarla hesaplanır.</li>
            </ul>
          </div>
        </Modal>
      )}
      {linking && (
        <LinkDialog
          item={linking}
          tenantQuery={tq}
          onClose={() => setLinking(null)}
          onLink={async (productId, units) => {
            await link(linking, productId, units);
            setLinking(null);
          }}
        />
      )}
    </>
  );
}

function LinkDialog(props: { item: ChannelProductView; tenantQuery: string; onClose: () => void; onLink: (productId: string, units: number) => Promise<void> }) {
  const guess = packCount(props.item.title);
  const [search, setSearch] = useState(props.item.barcode ?? '');
  const [term, setTerm] = useState(search);
  const [chosen, setChosen] = useState<ProductListItem | null>(null);
  const [units, setUnits] = useState(String(guess ?? 1));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const products = useLoad(() => api<ProductListItem[]>('GET', `/api/products?status=active${term ? `&q=${encodeURIComponent(term)}` : ''}${props.tenantQuery}`), [term]);
  const count = Number(units);
  const save = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      await props.onLink(chosen.id, count);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      wide
      title="Depodaki ürünü seçin"
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!chosen || busy || !Number.isInteger(count) || count < 1} onClick={() => void save()}>
            Eşleştir
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <div className="banner info-soft">
          <div>
            <b>{props.item.title}</b>
            <div className="mono" style={{ fontWeight: 500 }}>
              {props.item.channelSku}
              {props.item.barcode ? ` · ${props.item.barcode}` : ''}
            </div>
          </div>
        </div>
        <input className="input" placeholder="SKU, ad veya barkod ile ara" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        {!products.data ? (
          <Spinner />
        ) : products.data.length === 0 ? (
          <Empty title="Ürün bulunamadı">Aramayı değiştirin ya da ürünü önce katalogda tanımlayın.</Empty>
        ) : (
          <div className="pick-list">
            {products.data.slice(0, 50).map((p) => (
              <button key={p.id} className={`pick-row${chosen?.id === p.id ? ' on' : ''}`} onClick={() => setChosen(p)}>
                <ProductThumb name={p.name} imageUrl={p.imageUrl} small />
                <span className="what">
                  <b>{p.name}</b>
                  <span className="muted mono">
                    {p.sku}
                    {p.primaryBarcode ? ` · ${p.primaryBarcode}` : ''}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="row">
          <label htmlFor="link-units">
            <b>Pazaryerinde 1 satış =</b>
          </label>
          <input id="link-units" className="input" style={{ width: 90 }} inputMode="numeric" value={units} onChange={(e) => setUnits(e.target.value)} />
          <span>adet depo ürünü</span>
          {[1, 2, 3, 6, 12].map((n) => (
            <button key={n} className={`chip select${units === String(n) ? ' on' : ''}`} onClick={() => setUnits(String(n))}>
              {n}
            </button>
          ))}
        </div>
        <p className="muted" style={{ fontSize: 13 }}>
          "2'li set", "6'lı paket" gibi pazaryeri ürünlerinde adedi girin: siparişteki her set, depodan o kadar adet toplanır.
        </p>
      </div>
    </Modal>
  );
}
